import { Component } from 'san';
import * as _ from 'lodash';
import { Select } from 'santd';

import { IOrganization, IProjectAttribute, IProject, IUser, IProjectAttributesData } from 'src/models';
import { LEVEL_ONE_PROJECT_ID_LEVEL, ROOT_PROJECT_PUID } from 'src/consts';
import { TProjectAttributesScene, TProjectType } from 'src/types';
import { EProjectItemType } from 'src/enums';

import DatePicker from '../DatePicker/DatePicker';
import ProjectAttachments from '../ProjectAttachments/ProjectAttachments';
import LeanInputCell from '../LeanInputCell/LeanInputCell';
import LeanProjectHandler from '../LeanProjectHandler/LeanProjectHandler';
import LeanProjectHandlers from '../LeanProjectHandlers/LeanProjectHandlers';
import LeanProjectUnit from '../LeanProjectUnit/LeanProjectUnit';
import LeanProjectSelect from '../LeanProjectSelect/LeanProjectSelect';
import LeanDatePicker from '../LeanDatePicker/LeanDatePicker';
import LeanProjectTextArea from '../LeanProjectTextArea/LeanProjectTextArea';

import commonService from 'src/services/common.service';
import projectService from 'src/services/project.service';
import uiService from 'src/services/ui.service';
import auditService from 'src/services/audit.service';
import toasterService from 'src/services/toaster.service';
import organizationService from 'src/services/organization.service';
import userService from 'src/services/user.service';


const emptyProject: IProject = {
    id_level: LEVEL_ONE_PROJECT_ID_LEVEL,
    parent_puid: ROOT_PROJECT_PUID,
    title: null,
    _end_on: null, // 显示、编辑时，使用_end_on，来处理延期的项目的完成时间显示；提交时将其赋值给end_on
    group_codes: [],
    group_names: [],
    type_code: null,
    type_name: null,
    level_code: null,
    level_name: null,
    category_code: null,
    category_name: null,
    accountability_unit_id: null,
    accountability_unit: null,
    accountability_unit_full_name: null,
    person_liable_id: null,
    person_liable: null,
    assigner_id: null,
    assigner: null,
    manage_by: null,
    manager: null,
    leader_id: null,
    leader: null,
    target: null,
    deliverable: null,
    remark: null,
    is_need_receipt: true,
    is_secret: false,
    actions: {
        is_can_change: true,
        is_can_add: true,
        is_can_turn_receipt: true,
    },
    item_type: EProjectItemType.Project,
    // _is_need_receipt: 'true',
    _is_need_receipt: true,
    _is_secret: 'false',
    _attachments: [],

    // 会议决议项的属性
    other_person_liable_names: [],
    other_person_liable_accounts: [],
    accepter_id: null, // 验收人
    accepter: null,
    criteria: null, // 验收标准
    from_source_code: null
};

export default class Project extends Component {
    static components = {
        's-select': Select,
        's-select-option': Select.Option,
        'ui-date-picker': DatePicker,
        'ui-project-attachments': ProjectAttachments,
        'ui-lean-input-cell': LeanInputCell,
        'ui-lean-project-handler': LeanProjectHandler,
        'ui-lean-project-handlers': LeanProjectHandlers,
        'ui-lean-project-unit': LeanProjectUnit,
        'ui-lean-project-select': LeanProjectSelect,
        'ui-lean-date-picker': LeanDatePicker,
        'ui-lean-project-textarea': LeanProjectTextArea
    };

    static computed = {
        zippedProject(): IProject {
            const project = _.cloneDeep(this.data.get('project'));
            const type = this.data.get('type');
            const level = this.data.get('level');
            const group = this.data.get('group');
            const category = this.data.get('category');
            const _is_secret = this.data.get('_is_secret');
            const assigner = this.data.get('assigner');
            const manager = this.data.get('manager');
            const personLiable = this.data.get('personLiable');
            const leader = this.data.get('leader');
            const accepter = this.data.get('accepter');
            const otherPersonLiables = this.data.get('otherPersonLiables');
            const accountabilityUnit = this.data.get('accountabilityUnit');
            if (type) {
                project.type_code = type.code;
                project.type_name = type.name;
            } else {
                project.type_code = null;
                project.type_name = null;
            }
            if (level) {
                project.level_code = level.code;
                project.level_name = level.name;
            } else {
                project.level_code = null;
                project.level_name = null;
            }
            if (group) {
                project.group_codes = [group.code];
                project.group_names = [group.name];
            } else {
                project.group_codes = [];
                project.group_names = [];
            }
            if (category) {
                project.category_code = category.code;
                project.category_name = category.name;
            } else {
                project.category_code = null;
                project.category_name = null;
            }
            if (_is_secret) {
                project._is_secret = _is_secret.code;
            } else {
                project._is_secret = 'false';
            }
            if (assigner) {
                project.assigner_id = assigner.user_id;
                project.assigner = assigner.name;
            } else {
                project.assigner_id = null;
                project.assigner = null;
            }
            if (manager) {
                project.manage_by = manager.user_id;
                project.manager = manager.name;
            } else {
                project.manage_by = null;
                project.manager = null;
            }
            if (personLiable) {
                project.person_liable_id = personLiable.user_id;
                project.person_liable = personLiable.name;
            } else {
                project.person_liable_id = null;
                project.person_liable = null;
            }
            if (leader) {
                project.leader_id = leader.user_id;
                project.leader = leader.name;
            } else {
                project.leader_id = null;
                project.leader = null;
            }
            if (accepter) {
                project.accepter_id = accepter.user_id;
                project.accepter = accepter.name;
            } else {
                project.accepter_id = null;
                project.accepter = null;
            }
            if (otherPersonLiables?.length) {
                project.other_person_liable_accounts = _.map(otherPersonLiables, 'user_id');
                project.other_person_liable_names = _.map(otherPersonLiables, 'name');
            } else {
                project.other_person_liable_accounts = [];
                project.other_person_liable_names = [];
            }
            if (accountabilityUnit) {
                project.accountability_unit_id = accountabilityUnit.org_number;
                project.accountability_unit = accountabilityUnit.org_name;
                project.accountability_unit_full_name = accountabilityUnit.org_full_path;
            } else {
                project.accountability_unit_id = null;
                project.accountability_unit = null;
                project.accountability_unit_full_name = null;
            }
            return project;
        },
        isValid() {
            const project: IProject = this.data.get('zippedProject');
            // 年度重点项目、月度重点项目、专项、公文项目
            return (
                !!project.title && project.title.trim() &&
                project.group_codes?.length &&
                project.group_codes[0] &&
                project._end_on &&
                project.assigner_id &&
                project.accountability_unit_id &&
                project.person_liable_id &&
                project.level_code &&
                project.category_code &&
                project.type_code &&
                project.manage_by &&
                project.target && project.target.trim()
            );
            // 会议决议项
        },
        // 允许变更“附件”（即新项目）
        enableChangeProjectAttachments() {
            const project = this.data.get('project');
            return projectService.getProjectIsNewSync(project);
        },
        // 允许变更“是否保密”（即新项目 && 允许下发保密项目）
        enableChangeIsSecret() {
            const project = this.data.get('project');
            return projectService.getProjectIsNewSync(project) && this.data.get('isSecretProjectEnabled');
        },
        // 允许重置（即非新项目）
        isShowReset() {
            const project = this.data.get('project');
            return !projectService.getProjectIsNewSync(project);
        }

    };

    constructor(options: any) {
        super(options);
    }

    initData(): {
        auditId: string; // {{}}
        dirtyProjects: IProject[]; // {{}}下发单、变更单中发生变化的项目（变更、新增）
        projectAttributesScene: TProjectAttributesScene;
        isShow: boolean;
        isNew: boolean;
        groups: IProjectAttribute[]; // 项目群
        levels: IProjectAttribute[]; // 项目级别
        types: IProjectAttribute[]; // 项目类型
        categories: IProjectAttribute[]; // 业务分类
        secrets: IProjectAttribute[];  // 是否保密
        project: IProject;
        parentProject: IProject;
        isSecretProjectEnabled: boolean; // 是否可以添加保密项目
        currentTypeNameDeleted?: string; // 当前项目类型已被禁用（需加删除线）
        currentLevelNameDeleted?: string; // 当前项目级别已被禁用（需加删除线）
        currentCategoryNameDeleted?: string; // 当前业务分类已被禁用（需加删除线）
        currentGroupNameDeleted?: string; // 当前项目群已被禁用（需加删除线）
        disabledUsers?: IUser[]; // 禁用的用户列表

        focusId?: string;   // focus的组件
        cachedPersons?: IUser[];  // 缓存用户列表
        cachedUnits?: IOrganization[];  // 缓存组织列表
        assigner: IUser;
        manager: IUser;
        personLiable: IUser;
        leader: IUser;
        accepter: IUser;
        otherPersonLiables: IUser[];
        accountabilityUnit: IOrganization;
        group: IProjectAttribute;
        level: IProjectAttribute;
        type: IProjectAttribute;
        category: IProjectAttribute;
        _is_secret: IProjectAttribute;
        isHelpExpand: boolean;
    } {
        return {
            auditId: null,
            dirtyProjects: [],
            projectAttributesScene: 'project-issue',
            isShow: false,
            isNew: true,
            groups: [],
            levels: [],
            types: [],
            categories: [],
            secrets: [{ code: 'true', name: '是' }, { code: 'false', name: '否' }] as IProjectAttribute[],
            project: emptyProject,
            parentProject: null,
            isSecretProjectEnabled: false,
            currentTypeNameDeleted: '',
            currentLevelNameDeleted: '',
            currentCategoryNameDeleted: '',
            currentGroupNameDeleted: '',

            focusId: null,
            cachedPersons: [],
            cachedUnits: [],
            assigner: null,
            manager: null,
            personLiable: null,
            leader: null,
            accepter: null,
            otherPersonLiables: null,
            accountabilityUnit: null,
            group: null,
            level: null,
            type: null,
            category: null,
            _is_secret: { code: 'false', name: '否' } as IProjectAttribute,
            isHelpExpand: false
        };
    }

    inited() {
        userService.getUsers({ page: 1, limit: 99999, is_disabled: true })
            .then(disabledUsers => {
                // 禁用人员列表
                this.data.set('disabledUsers', disabledUsers);
        });
    }

    // 获取项目群、项目级别、项目类型、业务分类下拉列表
    getProjectAttributes(): Promise<IProjectAttributesData> {
        const projectAttributesScene = this.data.get('projectAttributesScene');
        return projectService
            .getProjectAttributesData(projectAttributesScene)
            .then((projectAttributesData) => {
                let dataKey = 'groups';
                const oldGroups = this.data.get(dataKey);
                const groups = projectAttributesData[dataKey];
                this.data.splice(dataKey, [0, oldGroups.length, ...groups]);
                dataKey = 'levels';
                const oldLevels = this.data.get(dataKey);
                const levels = projectAttributesData[dataKey];
                this.data.splice(dataKey, [0, oldLevels.length, ...levels]);
                dataKey = 'types';
                const types = projectAttributesData[dataKey];
                const oldTypes = this.data.get(dataKey);
                this.data.splice(dataKey, [0, oldTypes.length, ...types]);
                dataKey = 'categories';
                const oldCategories = this.data.get(dataKey);
                const categories = projectAttributesData[dataKey];
                this.data.splice(dataKey, [0, oldCategories.length, ...categories]);
                return projectAttributesData;
            });
    }

    // 处理项目群由多选改为单选（历史数据有多选的，只取第一个项目群）
    patchProjectGroup(project: IProject): IProject {
        project.group_codes = (project.group_codes && project.group_codes.slice(0, 1)) || [];
        project.group_names = (project.group_names && project.group_names.slice(0, 1)) || [];
        return project;
    }
    //#endregion


    // 责任人
    onPersonLiableChange(user: IUser) {
        if (!user) {
            return;
        }
        // 新建项目 && 责任单位为空 ，自动带出责任单位
        const project = this.data.get('project');
        const accountabilityUnit = this.data.get('accountabilityUnit');
        let cachedUnits = this.data.get('cachedUnits');
        if (projectService.getProjectIsNewSync(project) && !accountabilityUnit) {
            organizationService.getOrganization(user.org_number)
                .then(unit => {
                    this.data.set('accountabilityUnit', unit);
                    cachedUnits.push(unit);
                    cachedUnits = _.unionBy((cachedUnits), 'org_number');
                    this.data.set('cachedUnits', cachedUnits)
                });
        }
    }

    // 创建项目（owner调用）
    create(parentProject?: IProject) {
        this.data.set('isShow', true);
        this.fire('show-change', true);
        this.data.set('isNew', true);
        if (parentProject) {
            this.createChildProject(parentProject);
        } else {
            this.createLevelOneProject();
        }
    }

    createLevelOneProject() {
        const project = _.cloneDeep(emptyProject);
        // 属性列表值只有1个时，直接填充
        this.getProjectAttributes().then((projectAttributesData) => {
            if (1 === projectAttributesData.groups.length) {
                project.group_codes = [projectAttributesData.groups[0].code];
                project.group_names = [projectAttributesData.groups[0].name];
            }
            if (1 === projectAttributesData.levels.length) {
                project.level_code = projectAttributesData.levels[0].code;
                project.level_name = projectAttributesData.levels[0].name;
            }
            if (1 === projectAttributesData.types.length) {
                project.type_code = projectAttributesData.types[0].code as TProjectType;
                project.type_name = projectAttributesData.types[0].name;
            }
            if (1 === projectAttributesData.categories.length) {
                project.category_code = projectAttributesData.categories[0].code;
                project.category_name = projectAttributesData.categories[0].name;
            }
            this.data.set('project', project);
            this.data.set('parentProject', null);
            this.initProjectAttributes(project);
            this.interceptTab(project);
        });
    }

    createChildProject(parentProject: IProject) {
        // 子项目继承父项目的属性
        const project = Object.assign({}, emptyProject, {
            id_level: parentProject.id_level + 1,
            parent_puid: parentProject.puid,
            group_codes: parentProject.group_codes,
            group_names: parentProject.group_names,
            type_code: parentProject.type_code,
            type_name: parentProject.type_name,
            level_code: parentProject.level_code,
            level_name: parentProject.level_name,
            category_code: parentProject.category_code,
            category_name: parentProject.category_name,
        });
        this.data.set('project', project);
        this.data.set('parentProject', parentProject);
        this.getProjectAttributes();
        this.initProjectAttributes(project);
        this.interceptTab(project);
    }

    // 变更项目（owner调用）
    update(project: IProject, parentProject: IProject) {
        // 处理项目群由多选改为单选
        project = this.patchProjectGroup(project);
        this.data.set('project', project);
        this.data.set('parentProject', parentProject);
        this.data.set('isShow', true);
        this.fire('show-change', true);
        this.data.set('isNew', false);
        this.getProjectAttributes();
        this.initProjectAttributes(project);
        this.interceptTab(project);
    }

    // 初始化项目的各处理人
    /*private*/ initHandlerAttributes(project: IProject) {
        this.data.set(
            'assigner',
            project.assigner_id ? { user_id: project.assigner_id, name: project.assigner } : null
        );
        this.data.set(
            'manager',
            project.manage_by ? { user_id: project.manage_by, name: project.manager } : null
        );
        this.data.set(
            'personLiable',
            project.person_liable_id ? { user_id: project.person_liable_id, name: project.person_liable } : null
        );
        this.data.set(
            'leader',
            project.leader_id ? { user_id: project.leader_id, name: project.leader } : null
        );
        this.data.set(
            'accepter',
            project.accepter_id ? { user_id: project.accepter_id, name: project.accepter } : null
        );
        if (project.other_person_liable_accounts?.length) {
            const persons: IUser[] = [];
            _.forEach(project.other_person_liable_accounts, (value, index) => {
                persons.push({ user_id: value, name: project.other_person_liable_names[index] })
            });
            this.data.set('otherPersonLiables', persons);
        } else {
            this.data.set('otherPersonLiables', []);
        }
    }

    // 初始化项目的各下拉列表属性（项目类型、是否保密、项目级别、业务分类、项目群）
    /*private*/ initSelectAttributes(project: IProject) {
        if (project.type_code) {
            this.data.set('type', { code: project.type_code, name: project.type_name });
        }
        if (project.level_code) {
            this.data.set('level', { code: project.level_code, name: project.level_name });
        }
        if (project.category_code) {
            this.data.set('category', { code: project.category_code, name: project.category_name });
        }
        if (project.group_codes?.length) {
            this.data.set('group', { code: project.group_codes[0], name: project.group_names[0] });
        }
    }

    // 初始化项目的责任单位
    /*private*/ initUnitAttributes(organization: IOrganization) {
        this.data.set('accountabilityUnit', organization);
    }

    // 初始化项目的是否保密属性
    /*private*/ initSecret(project: IProject) {
        const secrets = this.data.get('secrets');
        const _is_secret = _.find(secrets, { code: project._is_secret });
        this.data.set('_is_secret', _is_secret);
    }

    // 初始化缓存用户列表（使san Select label正常显示）
    /*private*/ initCachedPersons(project: IProject) {
        let persons: IUser[] = [];
        if (project.assigner_id) {
            persons.push({ user_id: project.assigner_id, name: project.assigner });
        }
        if (project.manage_by) {
            persons.push({ user_id: project.manage_by, name: project.manager });
        }
        if (project.person_liable_id) {
            persons.push({ user_id: project.person_liable_id, name: project.person_liable });
        }
        if (project.leader_id) {
            persons.push({ user_id: project.leader_id, name: project.leader });
        }
        if (project.accepter_id) {
            persons.push({ user_id: project.accepter_id, name: project.accepter });
        }
        if (project.other_person_liable_accounts?.length) {
            _.forEach(project.other_person_liable_accounts, (value, index) => {
                persons.push({ user_id: value, name: project.other_person_liable_names[index] });
            });
        }
        persons = _.uniqBy(persons, 'user_id');
        this.data.set('cachedPersons', persons);
    }

    // 初始化缓存组织列表（使san Select label正常显示）
    /*private*/ initCachedUnits(organization: IOrganization) {
        const units: IOrganization[] = [];
        units.push(organization);
        this.data.set('cachedUnits', units);
    }

    // 初始化项目的各个属性
    initProjectAttributes(project: IProject) {
        this.initSelectAttributes(project);
        this.initCachedPersons(project);
        this.initHandlerAttributes(project);
        this.initSecret(project);
        if (project.accountability_unit_id) {
            organizationService.getOrganization(project.accountability_unit_id)
                .then(organization => {
                    this.initCachedUnits(organization);
                    this.initUnitAttributes(organization);
                });
        }
    }


    submit() {
        const project = this.data.get('zippedProject');
        console.log(project);
        if (!projectService.verifyProjectSync(project, {
            groups: this.data.get('groups'),
            levels: this.data.get('levels'),
            types: this.data.get('types'),
            categories: this.data.get('categories')
        })) {
            return;
        }
        if (this.data.get('isNew')) {
            commonService
                .getNewId()
                .then((id) => {
                    project.puid = id;
                    return this.verifyAuditProjects(project);
                })
                .then((isValid) => {
                    if (isValid) {
                        this.fire('create-project', project);
                        this.cancel();
                    }
                });
        } else {
            this.verifyAuditProjects(project).then((isValid) => {
                if (isValid) {
                    this.fire('update-project', project);
                    this.cancel();
                }
            });
        }
    }

    cancel() {
        this.data.set('isShow', false);
        this.data.set('assigner', null);
        this.data.set('manager', null);
        this.data.set('personLiable', null);
        this.data.set('leader', null);
        this.data.set('accepter', null);
        this.data.set('otherPersonLiables', null);
        this.data.set('accountabilityUnit', null);
        this.data.set('group', null);
        this.data.set('level', null);
        this.data.set('type', null);
        this.data.set('category', null);
        this.data.set('_is_secret', { code: 'false', name: '否' });
        this.data.set('parentProject', null);
        this.data.set('focusId', null);
        const project = _.cloneDeep(emptyProject);
        this.data.set('project', project);
        this.fire('show-change', false);
    }

    reset() {
        projectService.getProjectByPuid(this.data.get('project').puid).then(originalProject => {
            let project = this.data.get('project');
            originalProject = this.patchProjectGroup(originalProject);  // 处理项目群由多选改为单选
            project = Object.assign({}, project, originalProject);
            this.data.set('project', project);
            this.initProjectAttributes(project);
            this.interceptTab(project);
            this.data.set('focusId', null);
            toasterService.show({
                type: 'success',
                title: '重置成功',
            });
        });
    }

    //#region 会议决议项、OA公文
    // 校验audit中的项目（下发单、变更单中的新建、变更的项目）
    private verifyAuditProjects(project: IProject /*当前项目*/): Promise<boolean> {
        project = _.cloneDeep(project);
        const auditId = this.data.get('auditId');
        if (!auditId) {
            return Promise.resolve(true);
        }
        let dirtyProjects = _.cloneDeep(this.data.get('dirtyProjects'));
        const index = _.findIndex(dirtyProjects, { puid: project.puid });
        if (-1 !== index) {
            dirtyProjects[index] = project;
        } else {
            dirtyProjects.push(project);
        }
        dirtyProjects = uiService.zipAuditProjectsIsNeedReceiptSync(dirtyProjects);
        dirtyProjects = uiService.zipAuditProjectsIsSecretSync(dirtyProjects);
        dirtyProjects = uiService.zipAuditProjectsEndOnSync(dirtyProjects);
        return auditService
            .verifyAuditProjects({ audit_id: auditId, projects: dirtyProjects })
            .then((result) => {
                if (result.is_invalid) {
                    toasterService.show({
                        type: 'danger',
                        title: result.message,
                    });
                }
                return !result.is_invalid;
            });
    }

    // # region focus/blur
    // 拦截 Tab 切换焦点
    interceptTab(project: IProject) {
        //  属性id列表（禁用的Tab时跳过）
        const idInfos: {
            id: string;
            disabled?: boolean;
        }[] = [
                {
                    id: 'type',
                    disabled: 'OASpecPlan' === project.type_code
                },
                {
                    id: 'secret',
                    disabled: !this.data.get('enableChangeIsSecret')
                },
                { id: 'title' },
                { id: 'target' },
                { id: 'deliverable' },
                { id: 'assigner' },
                { id: 'manager' },
                { id: 'personLiable' },
                { id: 'accountabilityUnit' },
                { id: 'leader' },
                { id: 'endOn' },
                { id: 'level' },
                { id: 'category' },
                { id: 'group' },
                { id: 'otherPersonLiables' },
                { id: 'accepter' },
                { id: 'criteria' },
                { id: 'fromSourceCode' },
                { id: 'remark' }
            ];

        document.onkeydown = (event) => {
            if ('Tab' !== event.key) {
                return;
            }
            event.preventDefault();
            const focusId = this.data.get('focusId');
            const index = _.findIndex(idInfos, { id: focusId });
            let idInfo;
            if (-1 === index) {
                idInfo = event.shiftKey ? idInfos[idInfos.length - 1] : idInfos[0];
            } else {
                idInfo = event.shiftKey
                    ? _.findLast(idInfos, item => !item.disabled, index - 1)
                    : _.find(idInfos, item => !item.disabled, index + 1);
            }
            this.data.set('focusId', idInfo?.id);
        };
    }

    onFocus(id: string) {
        this.data.set('focusId', id);
    }

    onBlur(id: string) {
        const focusId = this.data.get('focusId');
        if (id === focusId) {
            this.data.set('focusId', '');
        }
    }
    // #endregion

    toggleHelpExpand() {
        this.data.set('isHelpExpand', !this.data.get('isHelpExpand'));
    }

}
